import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TextField, InputAdornment } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import axios from "axios";
import { useGlobalData } from "../../../contexts/GlobalDataContext";
import { debounce } from "lodash";

const AddressTextField = ({
  id,
  label,
  name,
  autoComplete,
  autoFocus,
  endAdornmentIcon: EndAdornmentIcon = LocationOnOutlinedIcon,
  formik,
  type = "text",
  loginPage = false,
  placeholder,
  numberFlag = false,
  ...props
}) => {
  const { API_KEY } = useGlobalData();
  const inputRef = useRef(null);
  const listRef = useRef();
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // console.log(document.body.offsetHeight, "---offsetHeight ------");
  // console.log(window.innerHeight, "inner height");
  // console.log(window.scrollY, "scrollY");
  // useEffect(() => {
  //   document.addEventListener("mousemove", (e) => {
  //     if (e.clientY < 50) {
  //       console.log("popup");
  //       alert("dont close")
  //     }
  //   });
  // }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query) => {
        if (!query || query.length < 2) {
          setSuggestions([]);
          setShowSuggestions(false);
          return;
        }
        try {
          const response = await axios.get(
            `https://atlas.microsoft.com/search/address/json`,
            {
              params: {
                "api-version": "1.0",
                query,
                "subscription-key": API_KEY,
              },
            }
          );
          setSuggestions(response.data.results);
          setShowSuggestions(true);
        } catch (error) {
          console.error("Error fetching address suggestions: ", error);
        }
      }, 300),
    [API_KEY]
  );
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handlePlaceSelect = (suggestion) => {
    const { address } = suggestion;
    formik.setFieldValue(name, address?.freeformAddress || "");
    setSuggestions([]);
    setShowSuggestions(false);
    setActiveIndex(-1);
  };

  const handleChange = useCallback(
    (e) => {
      const inputValue = e.target.value;
      debouncedSearch(inputValue);
      formik?.handleChange(e);
    },
    [formik, debouncedSearch]
  );

  const handleBlur = useCallback(() => {
    setTimeout(() => setShowSuggestions(false), 200);
  }, []);
  const handleKeyDown = useCallback(
    (e) => {
      if (!showSuggestions || suggestions?.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) =>
          prev === suggestions?.length - 1 ? 0 : prev + 1
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((prev) =>
          prev <= 0 ? suggestions?.length - 1 : prev - 1
        );
      } else if (e.key === "Enter" && activeIndex >= 0) {
        e.preventDefault();
        handlePlaceSelect(suggestions[activeIndex]);
      }
    },
    [showSuggestions, suggestions, activeIndex]
  );

  useEffect(() => {
    if (listRef.current && activeIndex >= 0) {
      const activeItem = listRef.current.children[activeIndex];
      activeItem?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeIndex]);
  const suggestionsList = useMemo(() => {
    return suggestions.map((suggestion, index) => (
      <li
        key={index}
        // ref={index === activeIndex ? listRef : null}
        style={{
          display: "flex",
          alignItems: "center",
          fontFamily: "Urbane-Light",
          fontSize: "12px",
          padding: "0.5rem",
          cursor: "pointer",
          borderBottom: "1px solid #ddd",
          backgroundColor: index === activeIndex ? "#f0f0f0" : "#fff",
        }}
        onClick={() => handlePlaceSelect(suggestion)}
        onMouseDown={(e) => e.preventDefault()}
      >
        <LocationOnOutlinedIcon
          style={{ marginRight: "10px", color: "#999" }}
        />
        <div>
          <strong>{suggestion.address?.freeformAddress}</strong>
          <div style={{ color: "#888", fontSize: "12px" }}>
            {suggestion.address?.municipality || suggestion.address?.country}
          </div>
        </div>
      </li>
    ));
  }, [suggestions, activeIndex]);
  return (
    <div style={{ position: "relative" }}>
      <TextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        type={type}
        id={id}
        label={label}
        name={name}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={formik?.values[name]}
        onChange={handleChange}
        onBlur={(e) => {
          formik?.handleBlur(e);
          handleBlur();
        }}
        onKeyDown={handleKeyDown}
        error={formik?.touched[name] && Boolean(formik?.errors[name])}
        helperText={
          formik?.touched[name] && formik?.errors[name]
            ? formik.errors[name]
            : ""
        }
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {EndAdornmentIcon && (
                <EndAdornmentIcon style={{ fontSize: "18px" }} />
              )}
            </InputAdornment>
          ),
          style: {
            fontWeight: 300,
            height: "40px",
            fontFamily: "Urbane-Light",
          },
          inputProps: {
            ref: inputRef,
            style: {
              padding: "8px 14px",
              fontSize: "12px",
            },
          },
        }}
        {...props}
      />

      {showSuggestions && suggestions.length > 0 && (
        <ul
          ref={listRef}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            backgroundColor: "#fff",
            border: "1px solid #ddd",
            borderRadius: "4px",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 10,
            margin: 0,
            padding: 0,
            listStyleType: "none",
          }}
        >
          {suggestionsList}
        </ul>
      )}
    </div>
  );
};

export default memo(AddressTextField);
